import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  IconButton,
  MenuItem,
  CircularProgress,
  Chip,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Assessment as AssessmentIcon,
} from "@mui/icons-material";
import { adminAPI } from "../../services/api";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { formatDistanceToNow } from "date-fns";
import UserStats from "../../components/admin/UserStats";
import CustomContainer from "../../components/common/CustomContainer";

const UserManagement = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [formData, setFormData] = useState({
    username: "",
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    role: "user",
    isActive: true,
  });
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [selectedUserStats, setSelectedUserStats] = useState(null);
  const { showSnackbar } = useSnackbar();

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getUsers();
      setUsers(response.data);
    } catch (error) {
      showSnackbar("Failed to load users", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleDialogOpen = (user = null) => {
    if (user) {
      setSelectedUser(user);
      setFormData({
        username: user.username,
        firstName: user.first_name,
        lastName: user.last_name,
        email: user.email,
        password: "", // Don't show existing password
        role: user.role,
        isActive: user.is_active,
      });
    } else {
      setSelectedUser(null);
      setFormData({
        username: "",
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        role: "user",
        isActive: true,
      });
    }
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedUser(null);
  };

  const validateForm = () => {
    const errors = [];
    if (!formData.username.trim())
      errors.push(t("common.userManagement.validation.usernameRequired"));
    if (!formData.firstName.trim())
      errors.push(t("common.userManagement.validation.firstNameRequired"));
    if (!formData.lastName.trim())
      errors.push(t("common.userManagement.validation.lastNameRequired"));
    if (!formData.email.trim())
      errors.push(t("common.userManagement.validation.emailRequired"));
    if (!selectedUser && !formData.password)
      errors.push(t("common.userManagement.validation.passwordRequired"));

    if (errors.length > 0) {
      showSnackbar(errors.join(". "), "error");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      const userData = {
        username: formData.username,
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        role: formData.role,
        is_active: formData.isActive,
        password: formData.password, // Changed back to password instead of password_hash
      };

      if (selectedUser && !formData.password) {
        // Don't send password if updating user and no new password provided
        delete userData.password;
      }

      if (selectedUser) {
        await adminAPI.updateUser(selectedUser.id, userData);
        showSnackbar(
          t("common.userManagement.messages.updateSuccess"),
          "success"
        );
      } else {
        await adminAPI.createUser(userData);
        showSnackbar(
          t("common.userManagement.messages.createSuccess"),
          "success"
        );
      }
      handleDialogClose();
      fetchUsers();
    } catch (error) {
      showSnackbar(
        error.response?.data?.message ||
          t("common.userManagement.messages.operationFailed"),
        "error"
      );
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      await adminAPI.updateUser(userId, { is_active: !currentStatus });
      showSnackbar(
        t("common.userManagement.messages.updateStatusSuccess", {
          status: currentStatus ? "deactivated" : "activated",
        }),
        "success"
      );
      fetchUsers();
    } catch (error) {
      showSnackbar(
        t("common.userManagement.messages.updateStatusFailed"),
        "error"
      );
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm(t("common.userManagement.messages.deleteConfirm"))) {
      return;
    }

    try {
      await adminAPI.deleteUser(userId);
      showSnackbar(
        t("common.userManagement.messages.deleteSuccess"),
        "success"
      );
      fetchUsers();
    } catch (error) {
      showSnackbar(t("common.userManagement.messages.deleteFailed"), "error");
    }
  };

  const handleOpenStats = (user) => {
    setSelectedUserStats(user);
    setStatsDialogOpen(true);
  };

  const columns = [
    {
      field: "fullName",
      headerName: t("common.userManagement.fields.fullName"),
      flex: 1,
      valueGetter: (params) =>
        `${params.row.first_name} ${params.row.last_name}`,
    },
    {
      field: "email",
      headerName: t("common.userManagement.fields.email"),
      flex: 1,
    },
    {
      field: "role",
      headerName: t("common.userManagement.fields.role"),
      width: 120,
      renderCell: (params) => (
        <Chip
          label={t(`common.userManagement.roles.${params.value}`)}
          color={params.value === "admin" ? "primary" : "default"}
          size="small"
        />
      ),
    },
    {
      field: "is_active",
      headerName: t("common.userManagement.fields.status"),
      width: 120,
      renderCell: (params) => (
        <Chip
          label={t(
            `common.userManagement.status.${
              params.value ? "active" : "inactive"
            }`
          )}
          color={params.value ? "success" : "error"}
          size="small"
          icon={params.value ? <CheckCircleIcon /> : <BlockIcon />}
        />
      ),
    },
    {
      field: "last_login_at",
      headerName: t("common.userManagement.fields.lastLogin"),
      width: 150,
      valueGetter: (params) =>
        params.value
          ? formatDistanceToNow(new Date(params.value), { addSuffix: false })
          : t("common.userManagement.lastLogin.never"),
    },
    {
      field: "actions",
      headerName: t("common.userManagement.fields.actions"),
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => handleOpenStats(params.row)}
            color="info"
            title={t("common.userManagement.actions.viewStats")}
          >
            <AssessmentIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDialogOpen(params.row)}
            color="primary"
            title={t("common.userManagement.actions.editUser")}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() =>
              handleToggleUserStatus(params.row.id, params.row.is_active)
            }
            color={params.row.is_active ? "error" : "success"}
            title={t(
              `common.userManagement.actions.${
                params.row.is_active ? "deactivateUser" : "activateUser"
              }`
            )}
          >
            {params.row.is_active ? <BlockIcon /> : <CheckCircleIcon />}
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDeleteUser(params.row.id)}
            color="error"
            title={t("common.userManagement.actions.deleteUser")}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <CustomContainer>
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h4">{t("navigation.userManagement")}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleDialogOpen()}
        >
          {t("common.userManagement.addUser")}
        </Button>
      </Box>

      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <DataGrid
          rows={users}
          columns={columns}
          pageSize={pageSize}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[5, 10, 20, 50]}
          pagination
          disableSelectionOnClick
          autoHeight
          loading={loading}
          components={{
            Toolbar: GridToolbar,
          }}
          componentsProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
            },
          }}
          sx={{
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
          }}
        />
      </Paper>

      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedUser
            ? t("common.userManagement.editUser")
            : t("common.userManagement.addNewUser")}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label={t("common.userManagement.fields.username")}
              required
              value={formData.username}
              onChange={(e) =>
                setFormData({ ...formData, username: e.target.value })
              }
              fullWidth
            />
            <TextField
              label={t("common.userManagement.fields.firstName")}
              value={formData.firstName}
              onChange={(e) =>
                setFormData({ ...formData, firstName: e.target.value })
              }
              fullWidth
              required
            />
            <TextField
              label={t("common.userManagement.fields.lastName")}
              value={formData.lastName}
              onChange={(e) =>
                setFormData({ ...formData, lastName: e.target.value })
              }
              fullWidth
              required
            />
            <TextField
              label={t("common.userManagement.fields.email")}
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              fullWidth
              required
            />
            <TextField
              label={
                selectedUser
                  ? t("common.userManagement.fields.newPassword")
                  : t("common.userManagement.fields.password")
              }
              type="password"
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
              fullWidth
              required={!selectedUser}
            />
            <TextField
              select
              label={t("common.userManagement.fields.role")}
              value={formData.role}
              onChange={(e) =>
                setFormData({ ...formData, role: e.target.value })
              }
              fullWidth
            >
              <MenuItem value="user">
                {t("common.userManagement.roles.user")}
              </MenuItem>
              <MenuItem value="admin">
                {t("common.userManagement.roles.admin")}
              </MenuItem>
            </TextField>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) =>
                    setFormData({ ...formData, isActive: e.target.checked })
                  }
                />
              }
              label={t("common.userManagement.fields.active")}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>{t("common.cancel")}</Button>
          <Button onClick={handleSubmit} variant="contained">
            {selectedUser ? t("common.update") : t("common.create")}
          </Button>
        </DialogActions>
      </Dialog>

      {selectedUserStats && (
        <UserStats
          open={statsDialogOpen}
          onClose={() => {
            setStatsDialogOpen(false);
            setSelectedUserStats(null);
          }}
          userId={selectedUserStats.id}
          username={`${selectedUserStats.first_name} ${selectedUserStats.last_name}`}
        />
      )}
    </CustomContainer>
  );
};

export default UserManagement;
